import { useEffect, useRef, useState } from 'react';
import { MimeType, isImage, useLoadAsset } from '@facephi/inphinite-common';
import { ButtonIcon, FlexContainer, Label } from '@facephi/ui-react';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import {
  ModalOperationTitle,
  ModalOperationsDetailFooter,
  ModalOperationsDetailHeader,
  ModalOperationsDetailSource,
  ModalOperationsDetailStyles,
  ModalOperationsDetailZoom,
} from './Styles';
import { ZOOM } from '../../state/constants';

type Props = {
  onGoLeft?(): void;
  onGoRight?(): void;
  source: string;
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
  show?: boolean;
  onChangeShow(show: boolean): void;
  testId?: string;
  className?: string;
  mimeType: MimeType;
  disabled?: boolean;
  disabledGoLeft?: boolean;
  disabledGoRight?: boolean;
};

export const ModalOperationsDetail = ({
  source,
  show = false,
  onChangeShow,
  testId,
  className,
  onGoRight,
  onGoLeft,
  title,
  subtitle,
  mimeType,
  disabledGoLeft,
  disabledGoRight,
}: Props) => {
  const { t } = useTranslation();

  const [zoom, setZoom] = useState<number>(ZOOM.defaultZoom);
  const [innerShow, setInnerShow] = useState<boolean>(show);

  const { resourceUrl, hasError } = useLoadAsset(source, mimeType);

  const imageRef = useRef<HTMLImageElement>(null);
  const printImage = useReactToPrint({
    content: () => imageRef.current,
  });

  useEffect(() => {
    setZoom(ZOOM.defaultZoom);
  }, [source]);

  useEffect(() => {
    setInnerShow(show);
  }, [show]);

  const onCloseModal = () => {
    setInnerShow(false);
  };

  const setNewZoom = (newZoom: number) => {
    let absoluteZoom = newZoom - (newZoom % ZOOM.defaultFlow);
    absoluteZoom = absoluteZoom < ZOOM.minZoom ? ZOOM.minZoom : absoluteZoom;
    absoluteZoom =
      absoluteZoom > ZOOM.defaultScale ? ZOOM.defaultScale : absoluteZoom;
    setZoom(absoluteZoom);
  };

  const increaseZoom = () => {
    setNewZoom(zoom + ZOOM.defaultFlow);
  };

  const decreaseZoom = () => {
    setNewZoom(zoom - ZOOM.defaultFlow);
  };

  const downloadSource = () => {
    saveAs(resourceUrl || '', title);
  };

  return (
    <ModalOperationsDetailStyles
      testId={testId}
      className={className}
      onChangeShow={onChangeShow}
      show={innerShow}
    >
      <FlexContainer
        flexDirection="column"
        justifyContent="space-between"
        flex="1"
      >
        <ModalOperationsDetailHeader
          alignItems="center"
          justifyContent="space-between"
        >
          <ModalOperationTitle columnGap="1.6" alignItems="center">
            <ButtonIcon
              iconName="ArrowArcLeft"
              onClick={onCloseModal}
              variant="secondary"
            />
            <Label fontSize="14" semibold negative noWrap>
              {`${t(title)} ${subtitle || ''}`}
            </Label>
          </ModalOperationTitle>
          <FlexContainer columnGap="1.6">
            <ButtonIcon
              iconName="MagnifyingGlassMinus"
              onClick={decreaseZoom}
              disabled={zoom === ZOOM.minZoom}
              variant="secondary"
            />
            <ModalOperationsDetailZoom fontSize="12" negative>
              {Number(zoom.toFixed(2))}%
            </ModalOperationsDetailZoom>
            <ButtonIcon
              iconName="MagnifyingGlassPlus"
              onClick={increaseZoom}
              disabled={zoom === ZOOM.defaultScale}
              variant="secondary"
            />
          </FlexContainer>
          <FlexContainer columnGap="1.6">
            <ButtonIcon
              iconName="Printer"
              onClick={printImage}
              disabled={!resourceUrl || !isImage(mimeType)}
              variant="secondary"
            />
            <ButtonIcon
              iconName="DownloadSimple"
              onClick={downloadSource}
              disabled={!resourceUrl}
              variant="secondary"
            />
          </FlexContainer>
        </ModalOperationsDetailHeader>
        <ModalOperationsDetailSource
          ref={imageRef}
          resource={resourceUrl}
          title={title}
          zoom={zoom}
          mimeType={mimeType}
          hasAssetError={hasError}
          flexDirectionError="column"
          zoomable
          controls
          autoPlay
        />
        <ModalOperationsDetailFooter
          alignItems="center"
          justifyContent="center"
          columnGap="1.6"
        >
          {onGoLeft && onGoRight && (
            <>
              <ButtonIcon
                iconName="ArrowCircleLeft"
                onClick={onGoLeft}
                variant="secondary"
                disabled={disabledGoLeft}
              />
              <ButtonIcon
                iconName="ArrowCircleRight"
                onClick={onGoRight}
                variant="secondary"
                disabled={disabledGoRight}
              />
            </>
          )}
        </ModalOperationsDetailFooter>
      </FlexContainer>
    </ModalOperationsDetailStyles>
  );
};
